import { useState } from 'react';
import type { ReactNode } from 'react';
import { useLive, useRuntime } from './context';
import { taskFor } from '../outbox/tasks';
import type { OwnerTask } from '../outbox/tasks';
import { formatClock } from '../mirror/time';
import { BookSheet } from './BookSheet';
import type { BookSheetPrefill } from './BookSheet';
import { sourceWord } from './Today';
import { ParcelAttention, parcelsNeedingAttention, useParcelRetry, useParcels } from './Parcels';
import { CollisionAttention, useStaySync } from './FrontDesk';
import { DataRequestAttention, useDataRequests, useRunDataRequest } from './DataRequests';
import { useLocale, useT } from '../messages';

/**
 * Tasks — spec §8.3, on screen.
 *
 * > The server wins. A queued intent that no longer applies — the slot was taken
 * > by the partner while the iPad was offline — comes back rejected, and the PWA
 * > raises it as a task for the owner: *"This 2 PM was booked by the concierge
 * > while you were offline. Reschedule Dana?"*
 *
 * This screen is the honest cost of the model made visible. It is deliberately
 * not an error log: every card names a person, a time and what happened to it,
 * and offers the two things there are to do — book them somewhere else, or let
 * it go. There is no "retry" on a conflict, because retrying is exactly the
 * thing that cannot work: the room is occupied, and pretending otherwise is how
 * a device creates a booking the partner has already sold.
 *
 * Dismiss deletes the intent. It does not un-reject it and it does not write
 * anything to the cloud — the cloud already knows what it decided.
 */
export function Tasks(): ReactNode {
  const runtime = useRuntime();
  const t = useT();
  const locale = useLocale();
  const [rebooking, setRebooking] = useState<BookSheetPrefill | null>(null);

  const session = useLive(() => runtime.mirror.session(), null);
  const timezone = session?.tenant.timezone ?? 'UTC';
  const rejected = useLive(() => runtime.outbox.rejected(), []);
  // Both, out of one context. `t` writes the sentence and `locale` formats the
  // clock inside it, and passing them together is what stops a Spanish task
  // from carrying an English `2 PM`.
  const tasks: OwnerTask[] = rejected.map((entry) => taskFor(entry, { timezone, t, locale }));

  // Fulfillment wave 3: a parcel the drain gave up on is an owner's task too —
  // "did not go out" is the same kind of sentence as "did not go through".
  // `useParcels` asks nothing when the module is off.
  const currency = session?.tenant.currency ?? 'USD';
  const parcels = useParcels();
  const stuck = parcelsNeedingAttention(parcels.data?.parcels ?? []);
  const { retry, busy, refusal } = useParcelRetry(parcels.reload);

  // V3: a room sold twice is an owner's task too — "we and Airbnb both sold
  // the 6th" is the same kind of sentence as "the slot was taken while you
  // were offline", and it belongs on the same screen. `useStaySync` asks
  // nothing when lodging is off.
  const sync = useStaySync();
  const collisions = sync.data?.collisions ?? [];

  // GS5: a guest asked for her data, or to be forgotten (spec §7.6). An
  // owner's task with a thirty-day clock, on the same screen for the same
  // reason as the three above — somebody needs a decision. Running it is
  // owner-only on the server; the button says so for everybody else.
  const asks = useDataRequests();
  const dataRequests = asks.data?.data_requests ?? [];
  const isOwner = session?.user.role === 'owner';
  const dataRun = useRunDataRequest(asks.reload, t);

  if (
    tasks.length === 0 &&
    stuck.length === 0 &&
    collisions.length === 0 &&
    dataRequests.length === 0 &&
    dataRun.outcome === null
  ) {
    return (
      <section className="view">
        <h1>{t('tasks.nothingTitle')}</h1>
        <p className="empty">{t('tasks.nothingBody')}</p>
      </section>
    );
  }

  return (
    <section className="view">
      <h1>
        {t('tasks.heading', {
          count: tasks.length + stuck.length + collisions.length + dataRequests.length,
        })}
      </h1>

      <DataRequestAttention
        requests={dataRequests}
        run={dataRun.run}
        busy={dataRun.busy}
        refusal={dataRun.refusal}
        outcome={dataRun.outcome}
        isOwner={isOwner}
        timezone={timezone}
        t={t}
        locale={locale}
      />

      <CollisionAttention collisions={collisions} timezone={timezone} t={t} locale={locale} />

      <ParcelAttention
        parcels={stuck}
        retry={retry}
        busy={busy}
        refusal={refusal}
        currency={currency}
        timezone={timezone}
        t={t}
        locale={locale}
      />

      <ul className="task-list">
        {tasks.map((task) => (
          <li key={task.id} className="task">
            <h2>{task.title}</h2>
            <p className="task__message">{task.message}</p>

            {task.conflicts.length > 0 ? (
              <ul className="task__conflicts">
                {task.conflicts.map((conflict) => (
                  <li key={conflict.appointment_id}>
                    <span className={`badge badge--source-${conflict.source}`}>
                      {sourceWord(conflict.source, t)}
                    </span>
                    <span>
                      {formatClock(conflict.start, timezone, locale)} –{' '}
                      {formatClock(conflict.end, timezone, locale)}
                      {conflict.client_name === null ? '' : ` · ${conflict.client_name}`}
                      {conflict.service_name === null ? '' : ` · ${conflict.service_name}`}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}

            <div className="task__actions">
              {task.canReschedule ? (
                <button
                  type="button"
                  className="button button--primary"
                  onClick={() =>
                    setRebooking({
                      serviceId: task.prefill?.serviceId,
                      resourceId: task.prefill?.resourceId,
                      clientId: task.prefill?.clientId,
                      guestName: task.prefill?.guestName,
                      startIso: task.prefill?.startIso,
                      // 0048: the length and the attachments she had already
                      // chosen. A rebook that quietly dropped them would move
                      // the price on a guest who has been quoted one.
                      optionId: task.prefill?.optionId,
                      addOnItemIds: task.prefill?.addOnItemIds,
                      source: 'staff',
                    })
                  }
                >
                  {t('tasks.findAnother')}
                </button>
              ) : null}
              <button
                type="button"
                className="button button--ghost"
                onClick={() => void runtime.outbox.dismiss(task.id)}
              >
                {t('tasks.dismiss')}
              </button>
            </div>
          </li>
        ))}
      </ul>

      {rebooking !== null ? (
        <BookSheet
          prefill={rebooking}
          onDone={() => setRebooking(null)}
          onCancel={() => setRebooking(null)}
        />
      ) : null}
    </section>
  );
}
